import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  User, 
  Flame, 
  Star, 
  Trophy,
  LogIn,
  LogOut,
  Loader2,
  Sparkles,
  Zap,
} from 'lucide-react';

export const StudentCard = ({ 
  user,
  isAuthenticated,
  onSignIn,
  onSignOut,
  loading,
  gamification,
}) => {
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [authMode, setAuthMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');

    try {
      if (authMode === 'signin') {
        const result = await onSignIn?.(email, password);
        if (result?.success) {
          setShowAuthDialog(false);
          resetForm();
        } else {
          setAuthError(result?.error || 'שגיאה בהתחברות');
        }
      } else {
        // For demo, just show success
        setAuthError('');
        setShowAuthDialog(false);
        resetForm();
      }
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setDisplayName('');
    setAuthError('');
  };

  const getDisplayName = () => {
    if (user?.user_metadata?.display_name) return user.user_metadata.display_name;
    if (user?.email) return user.email.split('@')[0];
    return 'אורח';
  };

  const getLevelTitle = (level) => {
    const titles = {
      1: 'מתחיל',
      2: 'חובב',
      3: 'מתקדם',
      4: 'מיומן',
      5: 'מקצוען',
      6: 'מומחה',
      7: 'אשף',
      8: 'גורו',
      9: 'אגדה',
      10: 'מאסטר',
    };
    return titles[level] || titles[1];
  };

  return (
    <>
      <Card className="border-border/30 overflow-hidden shadow-lg">
        {/* Header gradient */}
        <div className="h-16 bg-gradient-to-l from-primary/20 via-accent/20 to-secondary/20" />
        
        <CardContent className="relative pt-0 -mt-8">
          {/* Avatar */}
          <div className="flex justify-center mb-3">
            <div className="relative">
              <Avatar className="h-16 w-16 border-4 border-card shadow-lg">
                <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white text-xl font-bold">
                  {isAuthenticated ? getDisplayName().charAt(0).toUpperCase() : '👤'}
                </AvatarFallback>
              </Avatar>
              {gamification?.enabled && gamification?.streak > 0 && (
                <div className="absolute -top-1 -right-1 bg-gamification-streak text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold animate-streak-fire shadow-md">
                  {gamification.streak}
                </div>
              )}
            </div>
          </div>

          {/* Name & Level */}
          <div className="text-center mb-4">
            <h3 className="font-bold text-lg text-foreground">
              {isAuthenticated ? getDisplayName() : 'שלום אורח!'}
            </h3>
            {gamification?.enabled && (
              <div className="flex items-center justify-center gap-2 mt-1">
                <Badge variant="outline" className="bg-gamification-level/10 text-gamification-level border-gamification-level/30">
                  <Star className="h-3 w-3 ml-1 fill-current" />
                  רמה {gamification.level} - {getLevelTitle(gamification.level)}
                </Badge>
              </div>
            )}
          </div>

          {/* Gamification Stats */}
          {gamification?.enabled && (
            <div className="space-y-3 mb-4">
              {/* XP Progress */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-muted-foreground">התקדמות לרמה הבאה</span>
                  <span className="font-bold text-gamification-xp">{gamification.xp} XP</span>
                </div>
                <Progress 
                  value={gamification.levelProgress} 
                  className="h-2 bg-muted"
                />
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center p-2 rounded-lg bg-gamification-xp/10">
                  <Sparkles className="h-4 w-4 mx-auto text-gamification-xp mb-1" />
                  <div className="text-xs font-bold">{gamification.xp}</div>
                  <div className="text-[10px] text-muted-foreground">XP</div>
                </div>
                <div className="text-center p-2 rounded-lg bg-gamification-streak/10">
                  <Flame className="h-4 w-4 mx-auto text-gamification-streak mb-1" />
                  <div className="text-xs font-bold">{gamification.streak}</div>
                  <div className="text-[10px] text-muted-foreground">רצף</div>
                </div>
                <div className="text-center p-2 rounded-lg bg-gamification-achievement/10">
                  <Trophy className="h-4 w-4 mx-auto text-gamification-achievement mb-1" />
                  <div className="text-xs font-bold">{gamification.achievements?.length || 0}</div>
                  <div className="text-[10px] text-muted-foreground">הישגים</div>
                </div>
              </div>
            </div>
          )}

          {/* Auth Button */}
          {isAuthenticated ? (
            <Button
              variant="outline"
              className="w-full"
              onClick={onSignOut}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin ml-2" />
              ) : (
                <LogOut className="h-4 w-4 ml-2" />
              )}
              התנתק
            </Button>
          ) : (
            <Button
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
              onClick={() => setShowAuthDialog(true)}
            >
              <LogIn className="h-4 w-4 ml-2" />
              התחבר
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Auth Dialog */}
      <Dialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <Zap className="h-8 w-8 text-white" />
              </div>
              ברוכים הבאים ל-PyZone!
            </DialogTitle>
            <DialogDescription className="text-center">
              התחבר כדי לשמור את ההתקדמות שלך
            </DialogDescription>
          </DialogHeader>

          <Tabs value={authMode} onValueChange={setAuthMode} className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">התחברות</TabsTrigger>
              <TabsTrigger value="signup">הרשמה</TabsTrigger>
            </TabsList>

            <form onSubmit={handleAuth}>
              <TabsContent value="signin" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="email">אימייל</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="example@email.com"
                    dir="ltr"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">סיסמה</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    dir="ltr"
                    required
                  />
                </div>
              </TabsContent>

              <TabsContent value="signup" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="displayName">שם תצוגה</Label>
                  <Input
                    id="displayName"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="השם שלך"
                    dir="auto"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signupEmail">אימייל</Label>
                  <Input
                    id="signupEmail"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="example@email.com"
                    dir="ltr"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signupPassword">סיסמה</Label>
                  <Input
                    id="signupPassword"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="לפחות 6 תווים"
                    dir="ltr"
                    required
                    minLength={6}
                  />
                </div>
              </TabsContent>

              {authError && (
                <p className="text-destructive text-sm mt-2 text-center">{authError}</p>
              )}

              <DialogFooter className="mt-6">
                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90"
                  disabled={authLoading}
                >
                  {authLoading && <Loader2 className="h-4 w-4 animate-spin ml-2" />}
                  {authMode === 'signin' ? 'התחבר' : 'הירשם'}
                </Button>
              </DialogFooter>
            </form>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
};
